import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang, canSeePrices } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ShieldAlert, FlaskConical, ArrowRight, Lock, FileText } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(null);
  const { add } = useCart();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(r.data.image_url);
      })
      .catch(() => setError("Product not found"));
    // `api` is a module-level axios singleton; setState setters are stable.
    // Only `slug` should trigger a re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl">404</h1>
        <Link to="/catalog" className="text-sm underline mt-4 inline-block">
          ← {t("nav.catalog")}
        </Link>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10">
        <div className="aspect-square bg-white border border-border animate-pulse" />
        <div className="space-y-4">
          <div className="h-6 bg-white border border-border w-1/3 animate-pulse" />
          <div className="h-12 bg-white border border-border animate-pulse" />
          <div className="h-24 bg-white border border-border animate-pulse" />
        </div>
      </div>
    );
  }

  const showPrices = canSeePrices(user);
  const canOrder = showPrices; // same gate

  const handleAdd = () => {
    add(product, qty);
    toast.success("✓", { description: `${product.name} × ${qty}` });
  };

  const images = [product.image_url, product.image_url_2].filter(Boolean);

  return (
    <div data-testid="product-detail" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square overflow-hidden border border-border bg-white">
            <img
              src={resolveImageUrl(activeImg || product.image_url)}
              alt={product.name}
              className="w-full h-full object-cover transition-opacity duration-300"
              data-testid="product-active-image"
            />
          </div>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-2" data-testid="product-thumbnails">
              {images.map((src, idx) => {
                const isActive = src === (activeImg || product.image_url);
                return (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setActiveImg(src)}
                    data-testid={`product-thumb-${idx}`}
                    aria-label={`Image ${idx + 1}`}
                    className={`aspect-square overflow-hidden border bg-white transition-all ${
                      isActive
                        ? "border-foreground ring-1 ring-foreground"
                        : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <img src={resolveImageUrl(src)} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="overline">{product.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mt-2">
            {product.name}
          </h1>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            {product.long_desc}
          </p>

          <div className="grid grid-cols-2 gap-3 mt-8">
            <div className="border border-border bg-white p-4">
              <div className="overline">{t("pd.presentation")}</div>
              <div className="font-mono text-sm mt-1">{product.presentation}</div>
            </div>
            <div className="border border-border bg-white p-4">
              <div className="overline">{t("pd.dose")}</div>
              <div className="font-mono text-sm mt-1">{product.dose}</div>
            </div>
            <div className="border border-border bg-white p-4">
              <div className="overline">{t("pd.stock")}</div>
              <div className="font-mono text-sm mt-1">
                {product.in_stock ? t("pd.stock.in") : t("pd.stock.out")}
              </div>
            </div>
            <div className="border border-border bg-white p-4">
              <div className="overline">{t("pd.unitPrice")}</div>
              {showPrices ? (
                <div className="font-mono text-sm mt-1" data-testid="pd-price">
                  ${product.price.toFixed(2)}
                </div>
              ) : (
                <div
                  className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground mt-1"
                  data-testid="pd-price-locked"
                >
                  <Lock className="w-3 h-3" />
                  {t("pd.unitPrice.locked")}
                </div>
              )}
            </div>
          </div>

          <AccessBlock
            user={user}
            canOrder={canOrder}
            qty={qty}
            setQty={setQty}
            product={product}
            handleAdd={handleAdd}
            navigate={navigate}
            t={t}
          />

          <div className="mt-10 flex items-center gap-2 text-xs text-muted-foreground">
            <FlaskConical className="w-4 h-4" />
            {t("pd.qa")}
          </div>

          {product.coa_url && (
            <a
              href={resolveImageUrl(product.coa_url)}
              target="_blank"
              rel="noreferrer"
              data-testid="pd-coa-link"
              className="mt-3 inline-flex items-center gap-2 text-xs underline underline-offset-4 text-foreground hover:text-[hsl(var(--accent))]"
            >
              <FileText className="w-3.5 h-3.5" />
              View Certificate of Analysis (PDF)
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function AccessBlock({ user, canOrder, qty, setQty, product, handleAdd, navigate, t }) {
  if (!user || user === false) {
    return (
      <div className="mt-8 border border-border bg-white p-5 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm">{t("pd.access.signin")}</p>
          <div className="mt-3 flex gap-3">
            <Link to="/login"><Button size="sm" variant="outline" className="rounded-md">{t("nav.signin")}</Button></Link>
            <Link to="/register"><Button size="sm" className="rounded-md">{t("home.cta.doctor")}</Button></Link>
          </div>
        </div>
      </div>
    );
  }
  if (!canOrder) {
    return (
      <div className="mt-8 border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 p-5">
        <p className="text-sm font-medium">{t("pd.access.verify")}</p>
        <ul className="text-xs text-muted-foreground mt-2 space-y-1">
          <li>• {t("pd.access.id")}: <span className="font-mono">{user.id_document_status || "none"}</span></li>
          <li>• {t("pd.access.license")}: <span className="font-mono">{user.license_status || "none"}</span></li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">{t("pd.access.note")}</p>
        <Button
          size="sm"
          className="mt-4 rounded-md"
          onClick={() => navigate("/dashboard/verification")}
          data-testid="goto-verification"
        >
          {t("pd.manage")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-8 flex items-center gap-3">
      <div className="flex items-center border border-border">
        <button
          data-testid="qty-decrement"
          onClick={() => setQty((q) => Math.max(1, q - 1))}
          className="w-10 h-11 hover:bg-secondary"
        >
          −
        </button>
        <span className="w-10 text-center font-mono text-sm" data-testid="qty-value">
          {qty}
        </span>
        <button
          data-testid="qty-increment"
          onClick={() => setQty((q) => Math.min(20, q + 1))}
          className="w-10 h-11 hover:bg-secondary"
        >
          +
        </button>
      </div>
      <Button
        size="lg"
        className="rounded-md h-11 px-6"
        onClick={handleAdd}
        data-testid="add-to-cart"
      >
        {t("pd.add")} — ${(product.price * qty).toFixed(2)}
      </Button>
    </div>
  );
}
